/* The notification list shown when the bell is opened */
import { Box, Icon, IconProps, Link, Modal, Spinner } from '@amzn/awsui-components-core';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { navigateWithEmbedParams } from 'src/utils/navigation-utils';
import { Status } from 'src/models/AuthContextModels';
import { RootState } from 'src/store/store';
import { getNotificationPresentation } from './notificationPresentation';
import { isUnread } from './notificationModels';

const MAX_LIST_HEIGHT = '60vh';

interface NotificationPanelProps {
  visible: boolean;
  onDismiss: () => void;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({ visible, onDismiss }) => {
  const navigate = useNavigate();
  const items = useSelector((state: RootState) => state.notificationsStore.items);
  const status = useSelector((state: RootState) => state.notificationsStore.status);

  const onRowClick = (route: string) => {
    onDismiss();
    navigateWithEmbedParams(navigate, route);
  };

  // Unread + clickable -> a real (blue) Link. Read (greyed) items stay clickable but render as muted
  // text, not a blue Link, so the read/unread distinction is visible (a Cloudscape Link forces its
  // own blue color and would otherwise override the greyed Box). Non-clickable items are plain text.
  const renderMessage = (message: string, route: string | null, greyed: boolean) => {
    if (!route) {
      return <span>{message}</span>;
    }
    if (greyed) {
      // A reset <button> (not a styled span) so it's natively focusable and responds to Enter/Space
      // — a plain onClick span isn't keyboard-accessible. Color is inherited so it stays greyed.
      return (
        <button
          type="button"
          style={{
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
            color: 'inherit',
            textAlign: 'left'
          }}
          onClick={() => onRowClick(route)}
        >
          {message}
        </button>
      );
    }
    return <Link onFollow={() => onRowClick(route)}>{message}</Link>;
  };

  // Only the FIRST load (no items yet) shows a spinner; a background re-fetch keeps the current list.
  const isInitialLoad = status === Status.Loading && items.length === 0;

  return (
    <Modal visible={visible} onDismiss={onDismiss} header="Notifications" size="large">
      {isInitialLoad ? (
        <Box textAlign="center" padding="l">
          <Spinner />
        </Box>
      ) : status === Status.Failed && items.length === 0 ? (
        <Box color="text-status-error" textAlign="center" padding="l">
          Unable to load notifications
        </Box>
      ) : items.length === 0 ? (
        <Box color="text-body-secondary" textAlign="center" padding="l">
          No notifications
        </Box>
      ) : (
        <div style={{ maxHeight: MAX_LIST_HEIGHT, overflowY: 'auto' }}>
          {items.map((n, index) => {
            const presentation = getNotificationPresentation(n);
            const greyed = !isUnread(n);
            return (
              <React.Fragment key={n.notification_id}>
                {index > 0 && <div style={{ borderTop: "1px solid var(--color-border-divider-default)", margin: "8px 0" }} />}
                <Box color={greyed ? 'text-body-secondary' : undefined} padding={{ vertical: 'xs' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                    <span style={{ flexShrink: 0 }}>
                      <Icon name={presentation.iconName as IconProps.Name} />
                    </span>
                    <span>{renderMessage(presentation.message, presentation.route, greyed)}</span>
                  </div>
                </Box>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </Modal>
  );
};
